/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import { ClipboardIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import {
  ActionType, ITableProps, SortingMode, kaReducer,
} from "ka-table";
import {
  hideLoading, loadData, showLoading, updateData, updatePageIndex, updatePagesCount,
} from "ka-table/actionCreators";
import { DispatchFunc } from "ka-table/types";
import {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import { useParams } from "react-router-dom";
import uuid from "react-uuid";
import useLocalStorageState from "use-local-storage-state";
import { ReactComponent as SlackIcon } from "../../../../../assets/images/slack.svg";
import { useAllAutomations, useAutomationLogs } from "../../../../../hooks/QueryHooks";
import EmptyTablePlaceholder from "../../../../../shared/table/EmptyTablePlaceHolder";
import KaTable, { IActionPayload } from "../../../../../shared/table/KaTable";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";

const SignalLogsTable = ({
  alert, status,
}: any) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const [page, setPage] = useState<number>(0);
  const allLogs: any = useAutomationLogs({
    workspaceId: workspace?.id,
    automationType: "signal",
    status: status === "all" ? undefined : status,
    automationId: alert === "all" ? undefined : alert,
    enabled: !!workspace?.id,
    page,
  });

  const [tableColumns, setTableColumns] = useState([
    {
      key: "created_at",
      dataType: "automation_timestamp",
      title: "Timestamp",
      isResizable: true,
      colGroup: { style: { minWidth: 50 } },
    }, {
      key: "automation_name",
      dataType: "string",
      title: "Signal Name",
      isResizable: true,
      colGroup: { style: { minWidth: 50 } },
      isSortable: true,
    }, {
      key: "status",
      dataType: "log_status",
      title: "Status",
      isResizable: true,
      colGroup: { style: { minWidth: 50 } },
      isSortable: true,
    },
  ]);

  const tableHeight = useMemo(() => window.innerHeight - 230, []);
  const tableRef = useRef<any>(null);

  const [tableProps, changeTableProps] = useState<ITableProps>({
    columns: tableColumns as any,
    columnResizing: true,
    columnReordering: true,
    height: tableHeight,
    rowKeyField: "randomId",
    singleAction: loadData(),
    sortingMode: SortingMode.Single,
    loading: {
      enabled: true,
      text: "Loading",
    },
    paging: {
      enabled: true,
      pageIndex: 0,
      pagesCount: 1,
      pageSize: 20,
    },
  });

  useLayoutEffect(() => {
    if (tableHeight) {
      changeTableProps((prevState: ITableProps) => ({
        ...prevState,
        height: tableHeight,
      }));
    }
  }, [tableHeight]);

  const params: any = useParams();
  const [total, setTotal] = useState<number>(0);

  const onColumnChange = (payload: IActionPayload) => {
    const {
      type, columnKey, width, targetColumnKey,
    } = payload;

    if (type === ActionType.ResizeColumn) {
      const newFields = tableColumns.map((field: any) => {
        if (field.key === columnKey) {
          field.width = width;
        }
        return field;
      });
      setTableColumns(newFields);
    }

    if (type === ActionType.ReorderColumns) {
      const columnIndex = tableColumns.findIndex((field: any) => field.key === columnKey);
      const targetColumnIndex = tableColumns.findIndex(
        (field: any) => field.key === targetColumnKey,
      );
      const newFields = [...tableColumns];
      const temp = newFields[columnIndex];
      newFields[columnIndex] = newFields[targetColumnIndex];
      newFields[targetColumnIndex] = temp;
      // shift the column to the target column index.
      setTableColumns(newFields);
    }
  };

  const kaDispatch: DispatchFunc = (action: any) => {
    changeTableProps((prevState: ITableProps) => kaReducer(prevState, action));
    if (action.type === ActionType.ResizeColumn || action.type === ActionType.ReorderColumns) {
      if (action.type === ActionType.ResizeColumn) {
        onColumnChange({
          type: action.type,
          columnKey: action.columnKey,
          width: action.width,
        });
      } else {
        onColumnChange({
          type: action.type,
          columnKey: action.columnKey,
          targetColumnKey: action.targetColumnKey,
        });
      }
    } if (action.type === ActionType.UpdatePageIndex) {
      // change the page index
      setPage(action.pageIndex);
    }
  };

  const setData = () => {
    const temp = allLogs?.data?.logs?.map((item: any) => {
      const value = { ...item };
      value.randomId = uuid();
      value.records_affected = value.extra_information.records_affected;
      return value;
    });
    if (temp?.length > 0) {
      setTotal(allLogs?.data?.total);
      kaDispatch(updateData(temp));
      if (allLogs?.data?.total < ((tableProps?.paging?.pageIndex || 0) * (tableProps?.paging?.pageSize || 1))) {
        kaDispatch(updatePageIndex(0));
      }
      kaDispatch(updatePagesCount(Math.ceil((allLogs?.data?.total || 1) / (tableProps?.paging?.pageSize ?? 1)) || 0));
      kaDispatch(hideLoading());
    } else {
      setTotal(0);
      kaDispatch(updatePageIndex(0));
      kaDispatch(updateData([]));
      kaDispatch(updatePagesCount(1));
      kaDispatch(hideLoading());
    }
  };

  useEffect(() => {
    if (allLogs?.isLoading) {
      kaDispatch(showLoading());
    } else {
      setData();
    }
  }, [status, alert, allLogs?.isLoading, page]);

  return (
    <KaTable
      ref={tableRef}
      tableProps={tableProps}
      totalData={total}
      changeTableProps={changeTableProps}
      kaDispatch={kaDispatch}
      rowKeyField="randomId"
      itemType="Logs"
      onColumnChange={onColumnChange}
    />
  );
};

export default SignalLogsTable;
